import { useEffect, useMemo, useRef, useState } from 'react'
import { BarChart3, Bot, Filter, Plus, Search, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { useProfile } from '@/api/profile'
import {
  useAIAgents,
  useCreateAIAgent,
  useDeleteAIAgent,
  type AIAgent,
} from '@/api/aiAgents'
import { useCompany } from '@/api/company'
import { AgentTypeSelectionModal } from './aiAgents/AgentTypeSelectionModal'
import { AgentCreationAssistant } from './aiAgents/AgentCreationAssistant'
import { AgentCreatedModal } from './aiAgents/AgentCreatedModal'
import { TestCallModal } from './aiAgents/TestCallModal'
import { AnswerModeModal } from './aiAgents/AnswerModeModal'
import { AIAgentFormView } from './aiAgents/AIAgentFormView'
import { AIAgentCallHistory } from './aiAgents/AIAgentCallHistory'
import { TemplateWizardModal } from './aiAgents/TemplateWizardModal'
import { AgentCard } from './aiAgents/AgentCard'
import { useNavigate, useParams, useSearchParams } from 'react-router'

/** Vue principale AI Agents — port 1:1 de Next AIAgentsList.tsx. */
export function AIAgentsView() {
  const { t } = useTranslation()
  const { data: profile } = useProfile()
  const companyId = profile?.company
  const { data: agents = [], isLoading } = useAIAgents(companyId)
  const { data: company } = useCompany(companyId)
  const deleteAgent = useDeleteAIAgent(companyId)
  const createAgent = useCreateAIAgent(companyId)

  const [searchQuery, setSearchQuery] = useState('')
  const [showTypeSelection, setShowTypeSelection] = useState(false)
  const [showAgentCreated, setShowAgentCreated] = useState(false)
  const [createdAgentName, setCreatedAgentName] = useState('')
  const [createdAgentId, setCreatedAgentId] = useState<number | null>(null)
  // Agent ciblé par la modale « Appelez-moi » (test call)
  const [testCallAgent, setTestCallAgent] = useState<AIAgent | null>(null)
  // Agent ciblé par la modale « Quand doit-il répondre ? » (branchement)
  const [answerModeAgent, setAnswerModeAgent] = useState<AIAgent | null>(null)
  const [showTemplateWizard, setShowTemplateWizard] = useState(false)
  // Inbound agent: start empty or from a template (law firm)
  const [showAssistant, setShowAssistant] = useState(false)
  const [pendingDelete, setPendingDelete] = useState<AIAgent | null>(null)

  // Filtres avancés (dropdown au clic sur "Filters")
  const [showFilters, setShowFilters] = useState(false)
  const [filterType, setFilterType] = useState<'all' | 'inbound' | 'outbound'>('all')
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all')
  const [filterLanguage, setFilterLanguage] = useState<string>('all')
  const filtersRef = useRef<HTMLDivElement>(null)

  // Ferme le dropdown au clic extérieur
  useEffect(() => {
    if (!showFilters) return
    const handler = (e: MouseEvent) => {
      if (filtersRef.current && !filtersRef.current.contains(e.target as Node)) {
        setShowFilters(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [showFilters])

  // Sous-vue stockée dans l'URL pour permettre Back du navigateur + refresh + share.
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const { locale = 'fr' } = useParams<{ locale: string }>()
  const action = searchParams.get('action') // 'create-inbound'
  const editId = Number(searchParams.get('edit') || '') || null
  const historyId = Number(searchParams.get('history') || '') || null
  const analyticsId = Number(searchParams.get('analytics') || '') || null

  const editingAgent = editId ? agents.find((a) => a.id === editId) ?? null : null
  const historyAgent = historyId
    ? agents.find((a) => a.id === historyId) ?? null
    : null
  const analyticsAgent = analyticsId
    ? agents.find((a) => a.id === analyticsId) ?? null
    : null
  const showForm = action === 'create-inbound' || !!editingAgent

  // Helpers pour muter les query params (push history pour activer le Back).
  const pushParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(searchParams)
    if (value === null) next.delete(key)
    else next.set(key, value)
    // Nettoie les autres sous-vues pour éviter les conflits.
    ;['action', 'edit', 'history', 'analytics'].forEach((p) => {
      if (p !== key) next.delete(p)
    })
    setSearchParams(next)
  }
  const clearSubview = () => {
    const next = new URLSearchParams(searchParams)
    ;['action', 'edit', 'history', 'analytics'].forEach((p) => next.delete(p))
    setSearchParams(next)
  }

  // Liste des langues présentes parmi les agents — pour peupler le filtre dynamiquement.
  // Hook AVANT tout early return (sinon "Rendered fewer hooks than expected").
  const availableLanguages = useMemo(() => {
    const set = new Set(agents.map((a) => a.language).filter(Boolean))
    return Array.from(set).sort()
  }, [agents])

  // Combine recherche texte + filtres avancés. Null-safe sur phone_number_display
  // (un agent fraîchement créé peut ne pas avoir de numéro assigné).
  const filteredAgents = useMemo(() => {
    const q = searchQuery.toLowerCase()
    return agents.filter((agent) => {
      const matchSearch =
        q === '' ||
        (agent.name?.toLowerCase().includes(q) ?? false) ||
        (agent.phone_number_display?.toLowerCase().includes(q) ?? false)

      const matchType = filterType === 'all' || agent.call_mode === filterType
      const matchStatus =
        filterStatus === 'all' ||
        (filterStatus === 'active' && agent.is_active) ||
        (filterStatus === 'inactive' && !agent.is_active)
      const matchLanguage =
        filterLanguage === 'all' || agent.language === filterLanguage

      return matchSearch && matchType && matchStatus && matchLanguage
    })
  }, [agents, searchQuery, filterType, filterStatus, filterLanguage])

  // Compte combien de filtres actifs (hors search) pour le badge sur le bouton.
  const activeFilterCount =
    (filterType !== 'all' ? 1 : 0) +
    (filterStatus !== 'all' ? 1 : 0) +
    (filterLanguage !== 'all' ? 1 : 0)

  const resetFilters = () => {
    setFilterType('all')
    setFilterStatus('all')
    setFilterLanguage('all')
  }

  const isAgentIncomplete = (agent: AIAgent) => {
    if (agent.call_mode === 'outbound') {
      return (
        !agent.google_calendar ||
        (!agent.can_cancel_appointments && !agent.can_reschedule_appointments)
      )
    }
    return false
  }

  // Si on est en mode form (création ou édition), prend la place du contenu.
  // Ce return DOIT venir après tous les hooks ci-dessus.
  if (showForm) {
    return (
      <AIAgentFormView
        // A fresh form for each agent.
        key={editingAgent?.id ?? 'new'}
        agent={editingAgent}
        initialTab={editingAgent && isAgentIncomplete(editingAgent) ? 'actions' : undefined}
        onSuccess={clearSubview}
        onCancel={clearSubview}
      />
    )
  }

  const handleAgentTypeSelect = (type: 'inbound' | 'outbound') => {
    setShowTypeSelection(false)
    if (type === 'outbound') {
      setShowTemplateWizard(true)
    } else {
      // Entrant : l'assistant de création (point de départ, site, pages, puis l'agent)
      setShowAssistant(true)
    }
  }

  // L'agent créé par l'assistant s'ouvre, au besoin sur un appel d'essai (?tester=1).
  const openCreatedAgent = (agentId: number, test: boolean) => {
    setShowAssistant(false)
    const next = new URLSearchParams(searchParams)
    ;['action', 'history', 'analytics'].forEach((p) => next.delete(p))
    next.set('edit', String(agentId))
    if (test) next.set('tester', '1')
    setSearchParams(next)
  }

  const handleTemplateComplete = async (cfg: {
    template_id: string
    template_config: import('@/lib/agentTemplates').TemplateConfig
    first_message: string
    system_prompt: string
    voice_id: string
    language: string
  }) => {
    try {
      const created = await createAgent.mutateAsync({
        name: cfg.template_config.agent_name,
        call_mode: 'outbound',
        agent_template: cfg.template_id,
        template_config: cfg.template_config as unknown as Record<string, unknown>,
        first_message: cfg.first_message,
        system_prompt: cfg.system_prompt,
        voice_id: cfg.voice_id,
        language: cfg.language,
      })
      setCreatedAgentName(cfg.template_config.agent_name)
      setCreatedAgentId(created?.id ?? null)
      setShowTemplateWizard(false)
      setShowAgentCreated(true)
    } catch {
      toast.error(t('manager.aiAgents.unableToCreateAgent'))
    }
  }

  // Says what deleting really does (server: ai_agent_detail DELETE): no number is
  // ever released, an agent's own line or the main number goes back to usual handling,
  // and a flow step that used the agent moves on to its next step.
  const deleteDescription = (agent: AIAgent | null) => {
    if (!agent) return ''
    const name = agent.name
    const intro = agent.phone_number
      ? t('manager.aiAgents.deleteConfirmOwnLine', { name, number: agent.phone_number_display })
      : agent.uses_company_main_number
        ? t('manager.aiAgents.deleteConfirmMainNumber', { name })
        : t('manager.aiAgents.deleteConfirmNoNumber', { name })
    const flows = agent.answers_in_flows ?? []
    const flowLine = flows.length
      ? t('manager.aiAgents.deleteConfirmFlow', {
          count: flows.length,
          flows: flows.map((flow) => t('manager.aiAgents.deleteConfirmFlowName', { name: flow })).join(', '),
        })
      : null
    return [intro, flowLine, t('manager.aiAgents.deleteConfirmFinal')].filter(Boolean).join(' ')
  }

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return
    try {
      await deleteAgent.mutateAsync(pendingDelete.id)
      toast.success(t('manager.aiAgents.agentDeletedSuccess'))
    } catch {
      toast.error(t('manager.aiAgents.unableToDeleteAgent'))
    } finally {
      setPendingDelete(null)
    }
  }

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="px-6 lg:px-8 py-6 space-y-4 sm:space-y-6">
          {/* Header */}
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">
                {t('manager.aiAgents.sectionTitle')}
              </h1>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                {t('manager.aiAgents.sectionSubtitle')}
              </p>
            </div>
            {agents.length > 0 && (
              <Button
                type="button"
                onClick={() => setShowTypeSelection(true)}
                className="bg-blue-600 hover:bg-blue-700 shadow-sm text-sm"
              >
                <Plus className="h-4 w-4 mr-2" />
                {t('manager.aiAgents.createAgent')}
              </Button>
            )}
          </div>

          {isLoading ? (
            <div className="text-center py-12">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent" />
              <p className="mt-3 text-sm text-gray-500">
                {t('manager.aiAgents.loadingAgents')}
              </p>
            </div>
          ) : agents.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 p-6">
              <div className="text-center py-12">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-50 dark:bg-blue-500/10 mb-4">
                  <Bot className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  {t('manager.aiAgents.noAgentsConfigured')}
                </h3>
                <p className="text-gray-600 dark:text-slate-400 mb-6 max-w-sm mx-auto">
                  {t('manager.aiAgents.noAgentsDescription')}
                </p>
                <Button
                  type="button"
                  onClick={() => setShowTypeSelection(true)}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {t('manager.aiAgents.createFirstAgent')}
                </Button>
              </div>
            </div>
          ) : (
            <>
              {/* Search and Filters */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 bg-white dark:bg-slate-900 p-3 sm:p-4 rounded-lg border border-gray-200 dark:border-slate-800 shadow-sm">
                <div className="relative flex-1 sm:max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder={t('manager.aiAgents.searchAgent')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 h-10 text-sm"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <div ref={filtersRef} className="relative">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setShowFilters((v) => !v)}
                      className="h-10 flex-1 sm:flex-none text-sm"
                    >
                      <Filter className="h-4 w-4 mr-2" />
                      <span className="hidden sm:inline">
                        {t('manager.aiAgents.filters')}
                      </span>
                      {activeFilterCount > 0 && (
                        <span className="ml-2 px-1.5 py-0.5 text-[10px] leading-none font-bold rounded-full bg-[#E85D3A] text-white">
                          {activeFilterCount}
                        </span>
                      )}
                    </Button>

                    {/* Panel filtres */}
                    {showFilters && (
                      <div className="absolute right-0 top-full mt-2 z-30 w-72 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-xl p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
                            {t('manager.aiAgents.filtersPanel.title', 'Filtres')}
                          </h3>
                          {activeFilterCount > 0 && (
                            <button
                              type="button"
                              onClick={resetFilters}
                              className="text-xs text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white flex items-center gap-1"
                            >
                              <X className="w-3 h-3" />
                              {t('manager.aiAgents.filtersPanel.reset', 'Réinitialiser')}
                            </button>
                          )}
                        </div>

                        {/* Type */}
                        <div>
                          <label className="text-[10px] uppercase tracking-widest text-slate-400 dark:text-slate-500 font-medium block mb-1">
                            {t('manager.aiAgents.filtersPanel.type', 'Type')}
                          </label>
                          <select
                            value={filterType}
                            onChange={(e) =>
                              setFilterType(e.target.value as typeof filterType)
                            }
                            className="w-full text-sm bg-transparent border border-slate-200 dark:border-slate-700 rounded-lg px-2.5 py-1.5 text-slate-900 dark:text-white"
                          >
                            <option value="all">
                              {t('manager.aiAgents.filtersPanel.all', 'Tous')}
                            </option>
                            <option value="inbound">
                              {t('manager.aiAgents.filtersPanel.inbound', 'Entrants')}
                            </option>
                            <option value="outbound">
                              {t('manager.aiAgents.filtersPanel.outbound', 'Sortants')}
                            </option>
                          </select>
                        </div>

                        {/* Statut */}
                        <div>
                          <label className="text-[10px] uppercase tracking-widest text-slate-400 dark:text-slate-500 font-medium block mb-1">
                            {t('manager.aiAgents.filtersPanel.status', 'Statut')}
                          </label>
                          <select
                            value={filterStatus}
                            onChange={(e) =>
                              setFilterStatus(
                                e.target.value as typeof filterStatus,
                              )
                            }
                            className="w-full text-sm bg-transparent border border-slate-200 dark:border-slate-700 rounded-lg px-2.5 py-1.5 text-slate-900 dark:text-white"
                          >
                            <option value="all">
                              {t('manager.aiAgents.filtersPanel.all', 'Tous')}
                            </option>
                            <option value="active">
                              {t('manager.aiAgents.filtersPanel.active', 'Actifs')}
                            </option>
                            <option value="inactive">
                              {t('manager.aiAgents.filtersPanel.inactive', 'Inactifs')}
                            </option>
                          </select>
                        </div>

                        {/* Langue (dynamique) — caché s'il n'y a qu'une seule langue */}
                        {availableLanguages.length > 1 && (
                          <div>
                            <label className="text-[10px] uppercase tracking-widest text-slate-400 dark:text-slate-500 font-medium block mb-1">
                              {t('manager.aiAgents.filtersPanel.language', 'Langue')}
                            </label>
                            <select
                              value={filterLanguage}
                              onChange={(e) => setFilterLanguage(e.target.value)}
                              className="w-full text-sm bg-transparent border border-slate-200 dark:border-slate-700 rounded-lg px-2.5 py-1.5 text-slate-900 dark:text-white"
                            >
                              <option value="all">
                                {t('manager.aiAgents.filtersPanel.all', 'Toutes')}
                              </option>
                              {availableLanguages.map((lang) => (
                                <option key={lang} value={lang}>
                                  {lang.toUpperCase()}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="text-xs sm:text-sm text-gray-600 dark:text-slate-400 whitespace-nowrap">
                    {filteredAgents.length}{' '}
                    {t('manager.aiAgents.agentLabel', {
                      count: filteredAgents.length,
                    })}
                  </div>
                </div>
              </div>

              {/* Deux sections : ceux qui répondent, ceux qui appellent */}
              {([
                ['inbound', t('manager.aiAgents.sections.entrants', 'Répond à vos appels')],
                ['outbound', t('manager.aiAgents.sections.sortants', 'Appelle pour vous')],
              ] as const).map(([mode, titre]) => {
                const liste = filteredAgents.filter((a) => (a.call_mode ?? 'inbound') === mode)
                if (liste.length === 0) return null
                return (
                  <section key={mode} className="space-y-2">
                    <h2 className="text-[13px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                      {titre} <span className="text-slate-400 dark:text-slate-500 font-normal">· {liste.length}</span>
                    </h2>
                    <div className="space-y-2">
                      {liste.map((agent) => (
                        <AgentCard
                          key={agent.id}
                          agent={agent}
                          onEdit={() => pushParam('edit', String(agent.id))}
                          onTest={() => setTestCallAgent(agent)}
                          onAnswerMode={() => setAnswerModeAgent(agent)}
                          onAnalytics={() => pushParam('analytics', String(agent.id))}
                          onHistory={() => pushParam('history', String(agent.id))}
                          onDelete={() => setPendingDelete(agent)}
                        />
                      ))}
                    </div>
                  </section>
                )
              })}
              <div>

                {filteredAgents.length === 0 && searchQuery && (
                  <div className="text-center py-12 px-4">
                    <Search className="h-10 w-10 sm:h-12 sm:w-12 mx-auto mb-3 text-gray-300" />
                    <p className="text-sm sm:text-base text-gray-600 dark:text-slate-400">
                      {t('manager.aiAgents.noAgentMatchesSearch')}
                    </p>
                    <Button
                      type="button"
                      variant="link"
                      onClick={() => setSearchQuery('')}
                      className="mt-2 text-sm"
                    >
                      {t('manager.aiAgents.resetSearch')}
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <AgentTypeSelectionModal
        isOpen={showTypeSelection}
        onClose={() => setShowTypeSelection(false)}
        onSelectType={handleAgentTypeSelect}
      />

      <AgentCreationAssistant
        isOpen={showAssistant}
        onClose={() => setShowAssistant(false)}
        onBack={() => {
          setShowAssistant(false)
          setShowTypeSelection(true)
        }}
        companyId={companyId}
        companyWebsite={company?.website}
        onCreated={openCreatedAgent}
      />

      <TemplateWizardModal
        isOpen={showTemplateWizard}
        onClose={() => setShowTemplateWizard(false)}
        companyName={company?.name ?? ''}
        onComplete={(cfg) => void handleTemplateComplete(cfg)}
      />

      <AgentCreatedModal
        isOpen={showAgentCreated}
        onClose={() => setShowAgentCreated(false)}
        agentName={createdAgentName}
        onGoToActions={() => setShowAgentCreated(false)}
        onTestCall={
          createdAgentId
            ? () => {
                const agent = agents.find((a) => a.id === createdAgentId)
                setShowAgentCreated(false)
                setTestCallAgent(
                  agent ?? ({ id: createdAgentId, name: createdAgentName } as AIAgent),
                )
              }
            : undefined
        }
      />

      {answerModeAgent && (
        <AnswerModeModal
          isOpen={!!answerModeAgent}
          onClose={() => setAnswerModeAgent(null)}
          companyId={companyId}
          agentId={answerModeAgent.id}
          agentName={answerModeAgent.name}
          answersIn={{
            number: answerModeAgent.phone_number ? answerModeAgent.phone_number_display : answerModeAgent.uses_company_main_number ? t('manager.aiAgents.ligne.principal', 'Numéro principal') : null,
            flows: answerModeAgent.answers_in_flows ?? [],
          }}
          onOpenNumber={() => navigate(`/${locale}/manager?view=numbers`)}
        />
      )}

      {testCallAgent && (
        <TestCallModal
          isOpen={!!testCallAgent}
          onClose={() => setTestCallAgent(null)}
          companyId={companyId}
          agentId={testCallAgent.id}
          agentName={testCallAgent.name}
        />
      )}

      <AlertDialog
        open={!!pendingDelete}
        onOpenChange={(open) => !open && setPendingDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('manager.aiAgents.deleteConfirmTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteDescription(pendingDelete)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteAgent.isPending}>
              {t('common.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault()
                void handleConfirmDelete()
              }}
              disabled={deleteAgent.isPending}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {t('manager.aiAgents.deleteMenu')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AIAgentCallHistory
        open={!!historyAgent}
        onClose={() => clearSubview()}
        agentId={historyAgent?.id ?? null}
        agentName={historyAgent?.name ?? ''}
        companyId={companyId}
      />

      {/* Analytics placeholder — port du Next AIAgentsList qui affiche un teaser */}
      <Sheet
        open={!!analyticsAgent}
        onOpenChange={(o) => !o && clearSubview()}
      >
        <SheetContent side="right" className="w-full sm:max-w-lg flex flex-col">
          <SheetHeader className="px-6 py-4 border-b dark:border-slate-800 bg-gradient-to-r from-slate-50 via-white to-blue-50/40 dark:from-slate-900 dark:via-slate-900 dark:to-blue-500/10">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-blue-600 flex items-center justify-center shadow-md shadow-blue-600/20">
                <BarChart3 className="h-4 w-4 text-white" />
              </div>
              <SheetTitle className="text-base font-bold text-slate-900 dark:text-white tracking-tight">
                {analyticsAgent?.name}
              </SheetTitle>
              <SheetDescription className="sr-only">
                {t('manager.aiAgents.analyticsOf')} {analyticsAgent?.name}
              </SheetDescription>
            </div>
          </SheetHeader>
          <div className="flex-1 flex items-center justify-center p-8">
            <div className="text-center max-w-xs">
              <div className="h-16 w-16 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mx-auto mb-5">
                <BarChart3 className="h-7 w-7 text-slate-400" />
              </div>
              <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mb-2">
                {t('manager.aiAgents.analyticsSoon')}
              </h3>
              <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">
                {t('manager.aiAgents.analyticsDescription')}
              </p>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  )
}
